import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useState } from "react";
import { SubscriptionType } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";
import theme from "styles/theme";

import { SubscriptionColor } from "utils/color";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import CancelDialog from "./CancelDialog";
import ManageDialog from "./ManagePlanDialog";
import PlanBenefitsDescription from "./PlanBenefitsDescription";
import SubscriptionStatusText from "./SubscriptionStatusText";
import {
  disabledCurrentPlan,
  getCurrentPlanButton,
  getOtherPlanButton,
  getStatusChipLabel,
  isCurrentPlan,
} from "./helpers";
import styles from "./styles";

interface IProps {
  plan: Plans;
  loading: boolean;
  handleCheckout: (plan: Plans) => void;
  renewSubscription: (plan: Plans) => void;
  allLoading?: Map;
  notInterestedButton?: JSX.Element;
  upgradeDialogSource?: string;
  totalPlans: number;
}

export function Plan({
  loading,
  handleCheckout,
  renewSubscription,
  plan,
  allLoading,
  notInterestedButton,
  upgradeDialogSource,
  totalPlans,
}: IProps) {
  const { subscription, plans, isStarterPlan, emailSents } =
    useContext(SubscriptionContext);
  const { discoverTab } = useContext(DiscoverViewContext);
  const { handleManagePlan } = useSubscription();

  const isMediumScreen = useMediaQuery("(max-width:1300px)");
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));

  const isMobileScreen =
    useMediaQuery(theme.breakpoints.down("md")) ||
    (isLargeScreen && discoverTab !== null);

  const isStarterOnProPlan =
    isStarterPlan && (plan === Plans.PRO || plan === Plans.PRO_YEARLY);
  const stripePlan = subscription?.subscription;
  const price = plans ? plans[plan].price : 0;
  const resubscribe =
    getCurrentPlanButton(plan, subscription) === "Resubscribe";
  const renew =
    getCurrentPlanButton(plan, subscription) === "Renew Subscription";
  const retry = getCurrentPlanButton(plan, subscription) === "Retry";
  const isUserCurrentPlan = isCurrentPlan(subscription, plan);

  const [cancelDialog, setCancelDialog] = useState<boolean>(false);
  const [manageDialog, setManageDialog] = useState<boolean>(false);

  const backgroundColor = stripePlan?.status
    ? SubscriptionColor[stripePlan?.status]
    : "";

  const openCancel = () => {
    trackEvent(
      "Manage Subscription Dialog Cancel Subscription Button Pressed",
      {
        plan,
      },
    );
    setManageDialog(false);
    setCancelDialog(true);
  };

  const closeManageDialog = () => {
    trackEvent("Manage Subscription Dialog Closed", {
      plan,
    });
    setManageDialog(false);
  };

  const closeCancelDialog = () => {
    trackEvent("Cancel Subscription Dialog Closed", {
      plan,
    });
    setCancelDialog(false);
  };

  const handleModifyCurrentPlan = () => {
    if (resubscribe || retry) {
      handleCheckout(plan);
    } else if (renew) {
      renewSubscription(plan);
    } else {
      trackEvent("Manage Plan Button Pressed", {
        plan,
      });
      setManageDialog(true);
    }
  };

  const handleUpgradeTonNewPlan = () => {
    if (upgradeDialogSource === "Upgrade VA Button") {
      trackEvent("VA Onboarding Flow - Pay Now Button Pressed", { plan });
    } else {
      trackEvent(`Switch Plan Button Pressed`, {
        plan,
      });
    }
    handleCheckout(plan);
  };

  const renderPrice = () => {
    const currentPlanPrice = <>${price.toFixed(2)}/month</>;

    if (price === 0) {
      return "$0";
    } else if (plan === Plans.PRO_YEARLY || plan === Plans.VA_YEARLY) {
      const monthlyEquivalent = {
        [Plans.VA_YEARLY]: Plans.VA,
        [Plans.PRO_YEARLY]: Plans.PRO,
      };
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box sx={styles.strike}>
            <Box sx={{ color: "black" }}>
              ${plans && plans[monthlyEquivalent[plan]]?.price}/month
            </Box>
          </Box>
          {currentPlanPrice}
        </Box>
      );
    } else {
      return currentPlanPrice;
    }
  };

  const getGrid = () => {
    if (isMobileScreen || totalPlans === 1) return 12;
    if (isMediumScreen || totalPlans === 2) return 6;
    if (totalPlans === 3) return 4;
    return 6;
  };

  const planButtonText = getOtherPlanButton(
    plan,
    subscription?.subscription?.planName,
  );
  const label = getStatusChipLabel(subscription?.subscription?.status);

  const hasActiveSubscription =
    subscription &&
    subscription?.subscription?.planName === plan &&
    subscription?.isSubscribed;
  const isOnFreePlan =
    plan === Plans.FREE &&
    ((subscription && !subscription?.isSubscribed) || !subscription);

  const planLimitReset = (isOnFreePlan || hasActiveSubscription) && (
    <Typography component="div">
      You have sent{" "}
      <Box sx={{ color: "info.main" }} component="span">
        {emailSents}
      </Box>{" "}
      email(s) this month. Your plan's limits will reset on{" "}
      {subscription &&
        formatEpochTimestamp({
          epochTimestamp: subscription.creditsCycle[1],
          addYear: true,
        })}
      .
      <br />
      <br />
    </Typography>
  );

  return (
    <Grid
      item
      xs={12}
      md={getGrid()}
      container
      direction="column"
      sx={{ p: { xs: 0, sm: 6, lg: 3 } }}
    >
      <Grid item container xs="auto" sx={{ mb: 3 }}>
        <Grid item xs="auto" sx={{ mr: 1 }}>
          <Typography variant="h5">
            {plans && plans[plan].display_name}
            {isUserCurrentPlan && label && (
              <Chip
                size="small"
                sx={[styles.chip, { backgroundColor }]}
                label={label}
              />
            )}
            <br />
            {renderPrice()}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs sx={styles.textContainer}>
        <Typography component="div" sx={styles.planDescription}>
          <PlanBenefitsDescription plan={plan} />
        </Typography>
        {isUserCurrentPlan && (
          <SubscriptionStatusText
            status={
              subscription?.subscription?.status || SubscriptionType.TRIALING
            }
            cancelAt={subscription?.subscription?.cancelAt}
            warningText="Currently, all follow-ups and brand requests beyond your
                            cancellation date will be deleted."
          />
        )}

        {isStarterOnProPlan &&
          subscription?.subscription?.status === SubscriptionType.ACTIVE && (
            <Alert severity="info" sx={{ my: 2 }}>
              We have moved you from <strong>Starter tier</strong> to Pro tier
              because we no longer offer the Starter tier on our platform. You
              will still be charged the same rate as the Starter tier until you{" "}
              <strong>change or cancel</strong> your plan.
            </Alert>
          )}
      </Grid>
      <Grid item xs="auto">
        {isUserCurrentPlan ? (
          <>
            {planLimitReset}
            <LoadingButton
              loading={resubscribe && loading}
              color="primary"
              variant={resubscribe || renew ? "contained" : "outlined"}
              fullWidth
              disabled={disabledCurrentPlan(plan, subscription)}
              onClick={handleModifyCurrentPlan}
              disableElevation
            >
              {getCurrentPlanButton(plan, subscription)}
            </LoadingButton>
          </>
        ) : (
          <Grid container alignItems="center">
            {planLimitReset}
            {plan !== Plans.FREE && !subscription?.overridePlanName && (
              <LoadingButton
                loading={loading}
                onClick={handleUpgradeTonNewPlan}
                color={
                  planButtonText === "Choose This Plan"
                    ? "secondary"
                    : "primary"
                }
                variant={
                  planButtonText === "Choose This Plan"
                    ? "outlined"
                    : "contained"
                }
                fullWidth
                disableElevation
              >
                {planButtonText}
              </LoadingButton>
            )}
            {notInterestedButton}
          </Grid>
        )}

        <ManageDialog
          loading={loading}
          open={manageDialog}
          openCancel={openCancel}
          onClose={closeManageDialog}
          handleCheckout={handleCheckout}
          plan={plan}
          allLoading={allLoading}
        />

        <CancelDialog
          loading={loading}
          open={cancelDialog}
          onClose={closeCancelDialog}
          warningText="All follow-ups and brand requests beyond
          your cancellation date will be deleted."
          handleManagePlan={handleManagePlan}
          plan={plan}
        />
      </Grid>
    </Grid>
  );
}
